import { computed, readonly, ref } from 'vue';

enum ConnectionStatus {
  ONLINE = 'online',
  OFFLINE = 'offline',
  RECONNECTING = 'reconnecting',
  CONNECTED = 'connected',
}

type ConnectionStatusMonitoringOptions = {
  onOnline: () => void;
};

const getInitialBrowserOnlineStatus = (): boolean => {
  if (typeof navigator === 'undefined') {
    return true;
  }

  return navigator.onLine;
};

const isBrowserOnline = ref<boolean>(getInitialBrowserOnlineStatus());
const isWebSocketConnectionRequested = ref<boolean>(false);
const isWebSocketConnected = ref<boolean>(false);
const hasWebSocketConnected = ref<boolean>(false);

const status = computed<ConnectionStatus>(() => {
  if (!isBrowserOnline.value) {
    return ConnectionStatus.OFFLINE;
  }

  if (isWebSocketConnected.value) {
    return ConnectionStatus.CONNECTED;
  }

  if (isWebSocketConnectionRequested.value) {
    return ConnectionStatus.RECONNECTING;
  }

  return ConnectionStatus.ONLINE;
});

let stopMonitoring: (() => void) | null = null;

const useConnectionStatus = () => ({
  status: readonly(status),
  isBrowserOnline: readonly(isBrowserOnline),
  hasWebSocketConnected: readonly(hasWebSocketConnected),
});

const ConnectionStatusController = {
  startMonitoring: ({ onOnline }: ConnectionStatusMonitoringOptions): void => {
    stopMonitoring?.();

    if (typeof window === 'undefined') {
      return;
    }

    isBrowserOnline.value = navigator.onLine;

    const handleOnline = (): void => {
      isBrowserOnline.value = true;
      onOnline();
    };
    const handleOffline = (): void => {
      isBrowserOnline.value = false;
      isWebSocketConnected.value = false;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    stopMonitoring = (): void => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      stopMonitoring = null;
    };
  },

  stopMonitoring: (): void => {
    stopMonitoring?.();
  },

  setWebSocketConnectionRequested: (value: boolean): void => {
    isWebSocketConnectionRequested.value = value;

    if (!value) {
      isWebSocketConnected.value = false;
    }
  },

  setWebSocketConnected: (): void => {
    isWebSocketConnectionRequested.value = true;
    isWebSocketConnected.value = true;
    hasWebSocketConnected.value = true;
  },

  setWebSocketDisconnected: (): void => {
    isWebSocketConnected.value = false;
  },

  resetWebSocket: (): void => {
    isWebSocketConnectionRequested.value = false;
    isWebSocketConnected.value = false;
    hasWebSocketConnected.value = false;
  },
} as const;

export { ConnectionStatus, ConnectionStatusController, useConnectionStatus };
